import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import clsx from 'clsx';

import { useAuth } from '../auth/useAuth';

const PLAN_BADGE_CLASS: Record<string, string> = {
  Starter: 'bg-slate-100 text-slate-700 border-slate-200',
  Professional: 'bg-blue-50 text-blue-700 border-blue-200',
  Enterprise: 'bg-emerald-50 text-emerald-700 border-emerald-200',
};

const PLAN_BADGE_FALLBACK = 'bg-sky-50 text-sky-700 border-sky-200';

interface HeaderProps {
  /** Opens the mobile navigation drawer (hamburger, `md:hidden`). */
  onMenuClick?: () => void;
}

export function Header({ onMenuClick }: HeaderProps): JSX.Element {
  const { session, logout } = useAuth();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);

  if (session === null) {
    return <header className="h-14 border-b border-slate-200 bg-white" />;
  }

  const handleLogout = (): void => {
    setMenuOpen(false);
    logout();
    navigate('/login', { replace: true });
  };

  return (
    <header className="flex h-14 flex-none items-center justify-between gap-2 border-b border-slate-200 bg-white px-3 sm:px-4">
      <div className="flex min-w-0 items-center gap-2 sm:gap-3">
        {onMenuClick !== undefined ? (
          <button
            type="button"
            className="rounded-md p-2 text-slate-600 hover:bg-slate-100 md:hidden"
            onClick={onMenuClick}
            aria-label="Abrir menú"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="h-5 w-5">
              <path d="M4 6h16M4 12h16M4 18h16" strokeLinecap="round" />
            </svg>
          </button>
        ) : null}
        <span className="hidden text-sm font-medium text-slate-500 sm:inline">Tenant</span>
        <span className="min-w-0 truncate text-sm font-semibold text-slate-900">{session.tenantId}</span>
        <span
          className={clsx(
            'hidden rounded-full border px-2 py-0.5 text-xs font-medium sm:inline',
            PLAN_BADGE_CLASS[session.licenseType] ?? PLAN_BADGE_FALLBACK,
          )}
        >
          {session.licenseType}
        </span>
      </div>

      <div className="relative">
        <button
          type="button"
          className="flex items-center gap-2 rounded-md border border-slate-200 bg-white px-3 py-1.5 text-sm hover:bg-slate-50"
          onClick={() => setMenuOpen((open) => !open)}
          aria-haspopup="menu"
          aria-expanded={menuOpen}
        >
          <span
            aria-hidden
            className="flex h-6 w-6 items-center justify-center rounded-full bg-primary text-xs font-semibold uppercase text-white"
          >
            {session.userId.slice(0, 2)}
          </span>
          <span className="hidden sm:inline">{session.userId}</span>
          <svg viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4 text-slate-400">
            <path d="M5 8l5 5 5-5z" />
          </svg>
        </button>

        {menuOpen ? (
          <div
            role="menu"
            className="absolute right-0 z-20 mt-1 w-44 rounded-md border border-slate-200 bg-white py-1 shadow-lg"
          >
            <button
              type="button"
              role="menuitem"
              className="block w-full px-3 py-2 text-left text-sm hover:bg-slate-50"
              onClick={handleLogout}
            >
              Cerrar sesión
            </button>
          </div>
        ) : null}
      </div>
    </header>
  );
}
